import React, { useCallback, useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import Swal from "sweetalert2";
import "../style/styles.css";
import "../style/Contact.css";
import "../style/ContactCaptcha.css";

const INITIAL_FORM = {
  name: "",
  email: "",
  mobile: "",
  address: "",
  subject: "",
  message: "",
  website: "", // hidden anti-spam honeypot
  captchaAnswer: "",
};

const CAPTCHA_API = "/api/contact-captcha.php";
const SEND_MAIL_API = "/api/send-mail.php";

const ContactUs = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [emailStatus, setEmailStatus] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [captcha, setCaptcha] = useState({
    question: "",
    token: "",
    loading: false,
    error: "",
  });

  const loadCaptcha = useCallback(async () => {
    setCaptcha((previous) => ({
      ...previous,
      loading: true,
      error: "",
    }));

    try {
      const response = await fetch(CAPTCHA_API, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
        credentials: "same-origin",
      });

      const responseText = await response.text();
      const trimmedText = responseText.trim();
      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/json") && !trimmedText.startsWith("{")) {
        throw new Error("Captcha endpoint returned HTML instead of JSON. Please check /api/contact-captcha.php and .htaccess.");
      }

      const result = JSON.parse(trimmedText);

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to load captcha.");
      }

      setCaptcha({
        question: result.question || "",
        token: result.token || "",
        loading: false,
        error: "",
      });

      setFormData((previous) => ({
        ...previous,
        captchaAnswer: "",
      }));
    } catch (error) {
      setCaptcha({
        question: "",
        token: "",
        loading: false,
        error: error.message || "Captcha loading failed. Please refresh the page and try again.",
      });
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const contactDetails = [
    {
      title: "Address",
      text:
        "EBS IT Park,Ganesh Street,Near Uttar Banga Sambad Press,Madhya Santi Nagar, Siliguri,  West Bengal, INDIA, Pin-734004",
    },
    {
      title: "Office",
      text: "+91 353 405 5548",
    },
    {
      title: "Sales & Support",
      text: "+91 97337 21111, 99320 13501",
    },
    {
      title: "Email",
      text: "info@ebssoftware.co.in || ebs.slg@gmail.com",
    },
  ];

  const services = [
    "Customized software development",
    "ERP, POS, inventory and GST billing software",
    "Website and web application development",
    "Software implementation and training",
    "Software support and maintenance",
  ];

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const name = formData.name.trim();
    const email = formData.email.trim();
    const mobile = formData.mobile.trim();
    const message = formData.message.trim();
    const captchaAnswer = formData.captchaAnswer.trim();

    if (!name || !email || !mobile || !message) {
      Swal.fire({
        icon: "warning",
        title: "Required fields missing",
        text: "Please enter Name, Email, Mobile No. and Message.",
        confirmButtonText: "OK",
      });
      return false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid email address",
        text: "Please enter a valid email address.",
        confirmButtonText: "OK",
      });
      return false;
    }

    const mobilePattern = /^[0-9+\-\s()]{7,20}$/;

    if (!mobilePattern.test(mobile)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid mobile number",
        text: "Please enter a valid mobile number.",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!captcha.token || !captcha.question) {
      Swal.fire({
        icon: "warning",
        title: "Human verification missing",
        text: "Please reload the captcha and try again.",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!captchaAnswer) {
      Swal.fire({
        icon: "warning",
        title: "Human verification required",
        text: "Please answer the captcha question.",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!/^\d+$/.test(captchaAnswer)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid captcha answer",
        text: "Captcha answer should contain numbers only.",
        confirmButtonText: "OK",
      });
      return false;
    }

    return true;
  };

  const submitForm = async (event) => {
    event.preventDefault();

    if (isSending) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSending(true);
    setEmailStatus("");

    try {
      const response = await fetch(SEND_MAIL_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        cache: "no-store",
        credentials: "same-origin",
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          mobile: formData.mobile.trim(),
          address: formData.address.trim(),
          subject: formData.subject.trim() || "Website Contact Enquiry",
          message: formData.message.trim(),
          website: formData.website.trim(),
          captcha_token: captcha.token,
          captcha_answer: formData.captchaAnswer.trim(),
        }),
      });

      const result = await response.json().catch(() => ({
        success: false,
        message: "Invalid server response.",
      }));

      if (!response.ok || !result.success) {
        if (
          (result.message || "").toLowerCase().includes("captcha") ||
          (result.message || "").toLowerCase().includes("verification")
        ) {
          loadCaptcha();
        }

        throw new Error(result.message || "Email sending failed.");
      }

      setEmailStatus("success");
      setFormData(INITIAL_FORM);
      loadCaptcha();

      Swal.fire({
        icon: "success",
        title: "Email Sent Successfully",
        text:
          "Thank you for contacting EBS Software Solution. Our team will get back to you shortly.",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("PHP mail send error:", error);

      setEmailStatus("error");

      Swal.fire({
        icon: "error",
        title: "Email Sending Failed",
        text:
          error.message ||
          "Unable to send your message right now. Please try again or email us directly at info@ebssoftware.co.in.",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <main className="contact-page">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Contact Us | EBS Software Solution</title>
        <meta
          name="description"
          content="Contact EBS Software Solution, Siliguri, West Bengal, India for software development, GST billing, ERP, POS, inventory, web application and support."
        />
      </Helmet>

      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>

        <Container className="contact-hero-container">
          <Row className="align-items-center g-4">
            <Col lg={7}>
              <div className="contact-hero-content">
                <div className="contact-kicker">Contact EBS Software Solution</div>
                <h1>Contact Us</h1>
                <p>
                  EBS Software Solution is one of the fastest growing software
                  developments. The establishing objectives was to be the most
                  dependable organization providing impeccable service, premium
                  products and comprehensive solutions to the customers in India.
                </p>
              </div>
            </Col>

            <Col lg={5}>
              <div className="contact-hero-card">
                <span>Quick Contact</span>
                <strong>Sales & Support</strong>
                <p>
                  +91 97337 21111, 99320 13501
                  <br />
                  info@ebssoftware.co.in
                  <br />
                  ebs.slg@gmail.com
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="contact-main-section">
        <Container>
          <Row className="g-4 align-items-stretch">
            <Col lg={7}>
              <div className="contact-form-card">
                <div className="contact-section-label">Send Enquiry</div>
                <h2>Send Email</h2>
                <p className="contact-form-intro">
                  Fill the form below and send your requirement. Please enter a
                  valid email and mobile number so our team can contact you
                  quickly.
                </p>

                {emailStatus === "success" && (
                  <div className="contact-alert contact-alert-success">
                    Email sent successfully.
                  </div>
                )}

                {emailStatus === "error" && (
                  <div className="contact-alert contact-alert-error">
                    Email sending failed. Please try again.
                  </div>
                )}

                {captcha.error !== "" && (
                  <div className="contact-alert contact-alert-error">
                    {captcha.error}
                  </div>
                )}

                <form onSubmit={submitForm} noValidate>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      height: 0,
                      width: 0,
                      opacity: 0,
                    }}
                    aria-hidden="true"
                  />

                  <Row className="g-3">
                    <Col md={6}>
                      <label htmlFor="name">Name *</label>
                      <input
                        className="contact-input"
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                        autoComplete="name"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label htmlFor="email">Email *</label>
                      <input
                        className="contact-input"
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        autoComplete="email"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label htmlFor="mobile">Mobile No. *</label>
                      <input
                        className="contact-input"
                        type="tel"
                        id="mobile"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        placeholder="Enter mobile number"
                        autoComplete="tel"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label htmlFor="subject">Subject</label>
                      <input
                        className="contact-input"
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Software enquiry / Support / Demo"
                      />
                    </Col>

                    <Col md={12}>
                      <label htmlFor="address">Full Address</label>
                      <input
                        className="contact-input"
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter your full address"
                        autoComplete="street-address"
                      />
                    </Col>

                    <Col md={12}>
                      <label htmlFor="message">Message *</label>
                      <textarea
                        className="contact-input contact-textarea"
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Write your message or requirement"
                        rows="5"
                        required
                      />
                    </Col>

                    <Col md={12}>
                      <div className="contact-captcha-box">
                        <div className="contact-captcha-question-wrap">
                          <div>
                            <label htmlFor="captchaAnswer">
                              Human Verification *
                            </label>
                            <div className="contact-captcha-question">
                              {captcha.loading
                                ? "Loading captcha..."
                                : captcha.question || "Captcha not loaded"}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="contact-captcha-refresh"
                            onClick={loadCaptcha}
                            disabled={captcha.loading || isSending}
                          >
                            Refresh
                          </button>
                        </div>

                        <input
                          className="contact-input"
                          type="text"
                          id="captchaAnswer"
                          name="captchaAnswer"
                          value={formData.captchaAnswer}
                          onChange={handleChange}
                          placeholder="Enter answer"
                          inputMode="numeric"
                          autoComplete="off"
                          required
                        />
                      </div>
                    </Col>

                    <Col md={12}>
                      <button
                        type="submit"
                        className="contact-send-btn"
                        disabled={isSending || captcha.loading || !captcha.token}
                      >
                        {isSending ? "Sending Email..." : "Send Email"}
                      </button>
                    </Col>
                  </Row>
                </form>
              </div>
            </Col>

            <Col lg={5}>
              <div className="contact-info-card">
                <div className="contact-section-label">Company Details</div>
                <h2>EBS Software Solution</h2>

                <p>
                  EBS Software Solution,is one of the fastest growing software
                  developments.The establishing objectives was to be the most
                  dependable organization providing impeccable service, premium
                  products and comprehensive solutions to the customers in India.
                </p>

                <div className="contact-detail-list">
                  {contactDetails.map((item) => (
                    <div className="contact-detail-card" key={item.title}>
                      <span>{item.title}</span>
                      <strong>{item.text}</strong>
                    </div>
                  ))}
                </div>

                <div className="contact-service-card">
                  <h3>We can help you with</h3>
                  <ul>
                    {services.map((item) => (
                      <li key={item}>
                        <span>✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default ContactUs;
